/**
 * Real-time directional shadow maps, opt-in: the settings checkbox, or
 * `?shadows=1`. The game ships without them (see `sky.ts`) — a second full
 * scene pass for contrast baked vertex AO already fakes.
 *
 * A MODULE-LEVEL CONSTANT because the decision is BAKED: `shadowMap.enabled`,
 * every material's cast/receive flags and the light rig are set when the scene
 * is built, half of them by geometry builders that ran once. The settings panel
 * says the change takes effect on the next load. The URL parameter is a
 * force-ON override, like `?debug=1`, and must never reach game logic or the
 * protocol. Its own file because the builders (`mapMesh`, `props`, `signs`,
 * `character`) read it and `world.ts` imports all four; its only import is
 * `settings.ts`, import-safe under node. DEFAULT-OFF MUST BE BYTE-IDENTICAL:
 * every consumer is a plain `if (SHADOWS_ENABLED)`.
 */

import { useSettings } from "../settings.js";

/**
 * True with `?shadows=1` or the settings checkbox. Guarded for headless: with
 * no `window` or `localStorage` it is `false`, never a throw, so the smoke test
 * measures the default path.
 */
export const SHADOWS_ENABLED: boolean = readFlag() || readSetting();

/**
 * Whether it was the URL that turned them on — the settings panel says "forced
 * on by ?shadows=1" rather than promising an untick will help.
 */
export const SHADOWS_URL_FORCED: boolean = readFlag();

function readFlag(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return new URLSearchParams(window.location.search).get("shadows") === "1";
  } catch {
    // A stubbed `window` without a usable `location` (any future test harness)
    // gets the default, not an exception at module-eval time.
    return false;
  }
}

/**
 * The persisted setting as it stood at first import. `settings.ts` cannot throw
 * on init; the try/catch is belt-and-braces.
 */
function readSetting(): boolean {
  try {
    return useSettings.getState().shadows;
  } catch {
    return false;
  }
}

// --- Shadow camera tuning: here rather than `palette.ts`, since none of it
// describes the default look.

/**
 * Half-width of the sun's orthographic shadow box, metres. The box FOLLOWS the
 * player (`updateShadowFocus`) — a fixed box over 2.4 km would spend 2048² on
 * metre texels. 90 m, since `FOG_DENSITY` has eaten most contrast by ~120 m.
 */
export const SHADOW_HALF_EXTENT = 90;

/** Shadow map resolution. 2048² over a 180 m box = 8.8 cm per texel. */
export const SHADOW_MAP_SIZE = 2048;

/**
 * How far up-sun the light is parked from the focus, metres, and the depth
 * slab. 180 ± 170 m swallows alpine relief plus roofs; an orthographic camera's
 * depth is linear, so a generous slab is cheap.
 */
export const SHADOW_LIGHT_DISTANCE = 180;
export const SHADOW_NEAR = 10;
export const SHADOW_FAR = 350;

/**
 * Shadow acne bias for THIS scene — a flat-shaded, vertex-coloured merged mesh
 * is the worst case. `normalBias` does the work: one texel is 8.8 cm, PCF
 * widens it to two or three, and 0.15 m (~1.7 texels) is the smallest value
 * that holds without sliding a character's shadow off their feet (the half
 * metre often recommended would). `bias` is a small constant top-up: −0.0004
 * over the 340 m slab is ~14 cm along the light, for near-grazing faces.
 */
export const SHADOW_BIAS = -0.0004;
export const SHADOW_NORMAL_BIAS = 0.15;

/**
 * The focus is snapped to a grid this many texels wide before the light moves —
 * without it the shadows CRAWL as you walk. 23 texels ≈ 2.02 m, and an INTEGER
 * number of texels is what matters: the depth map is literally the same image
 * between snaps.
 */
export const SHADOW_SNAP_TEXELS = 23;
